/**
 * HALO - Privacy Settings Section
 * Privacy and data sharing preferences
 */

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff, Smile, Activity, Zap, Hand, UserCircle, CheckCircle2, AlertCircle, Info } from "lucide-react";
import { Card } from "@/components/ui/card";
import { getPrivacySettings, updatePrivacySettings, PrivacySettings } from "@/lib/supabase";

interface PrivacySectionProps {
  userId: string;
}

interface PrivacyOption {
  key: keyof PrivacySettings;
  label: string;
  description: string;
  icon: React.ElementType;
}

const privacyOptions: PrivacyOption[] = [
  {
    key: "share_emotion",
    label: "Share Emotion Data",
    description: "Allow managers to see your detected emotional states (Focused, Happy, Stressed, etc.)",
    icon: Smile,
  },
  {
    key: "share_posture",
    label: "Share Posture Data",
    description: "Allow managers to see your posture score and ergonomic recommendations",
    icon: Activity,
  },
  {
    key: "share_fatigue",
    label: "Share Fatigue Data",
    description: "Allow managers to see your fatigue index and eye strain metrics",
    icon: Zap,
  },
  {
    key: "share_activity",
    label: "Share Hand Activity",
    description: "Allow managers to see your hand activity (typing, mouse, phone detection)",
    icon: Hand,
  },
  {
    key: "blur_avatar",
    label: "Blur Avatar in Team View",
    description: "Show a blurred version of your avatar to maintain privacy",
    icon: UserCircle,
  },
];

export function PrivacySection({ userId }: PrivacySectionProps) {
  const [settings, setSettings] = useState<PrivacySettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, [userId]);

  const loadSettings = async () => {
    try {
      const data = await getPrivacySettings(userId);
      if (data) {
        setSettings(data);
      }
    } catch (err) {
      console.error("[PrivacySection] Error loading settings:", err);
      setMessage({ type: "error", text: "Failed to load privacy settings" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (key: keyof PrivacySettings) => {
    if (!settings) return;

    setSettings((prev) => {
      if (!prev) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  };

  const handleSave = async () => {
    if (!settings) return;

    setIsSaving(true);
    setMessage(null);

    try {
      const result = await updatePrivacySettings(userId, settings);

      if (result.success) {
        setMessage({ type: "success", text: "Privacy settings updated successfully" });
      } else {
        setMessage({ type: "error", text: result.error || "Failed to update settings" });
      }
    } catch (err) {
      console.error("[PrivacySection] Error saving settings:", err);
      setMessage({ type: "error", text: "Failed to update settings" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-signal-crimson mx-auto mb-4" />
        <p className="text-muted-slate">Failed to load privacy settings</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <Card className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Eye className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Privacy Settings</h3>
            <p className="text-sm text-muted-slate">Control what data is shared with your team</p>
          </div>
        </div>

        <div className="mb-6 p-4 rounded-xl bg-surface-elevated border border-glass">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">Privacy-First Design</h4>
              <p className="text-sm text-muted-slate">
                HALO is designed with privacy at its core. You have full control over what data is shared
                with your managers and team. Your raw wellness data is never shared without your consent.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {privacyOptions.map((option) => {
            const Icon = option.icon;
            const isEnabled = settings[option.key];

            return (
              <motion.div
                key={option.key}
                whileHover={{ scale: 1.01 }}
                className={`p-4 rounded-xl border transition-colors cursor-pointer ${
                  isEnabled
                    ? "bg-primary/5 border-primary/20"
                    : "bg-surface-elevated border-glass-grey"
                }`}
                onClick={() => handleToggle(option.key)}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                      isEnabled ? "bg-primary/10 text-primary" : "bg-muted-slate/10 text-muted-slate"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-semibold text-foreground">{option.label}</h4>
                      <div
                        className={`w-12 h-6 rounded-full transition-colors relative ${
                          isEnabled ? "bg-primary" : "bg-muted-slate/30"
                        }`}
                      >
                        <motion.div
                          className="w-5 h-5 bg-foreground rounded-full absolute top-0.5"
                          animate={{ left: isEnabled ? "26px" : "2px" }}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      </div>
                    </div>
                    <p className="text-sm text-muted-slate mt-1">{option.description}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-6 p-4 rounded-xl flex items-center gap-3 ${
              message.type === "success"
                ? "bg-signal-green/10 text-signal-green"
                : "bg-signal-crimson/10 text-signal-crimson"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="text-sm font-medium">{message.text}</span>
          </motion.div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2.5 bg-primary hover:bg-primary/90 disabled:bg-primary/50 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
          >
            {isSaving ? "Saving..." : "Save Privacy Settings"}
          </button>
        </div>
      </Card>

      <Card className="p-6 border-glass">
        <div className="flex items-start gap-3">
          <EyeOff className="w-5 h-5 text-muted-slate mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-1">Complete Privacy Option</h4>
            <p className="text-sm text-muted-slate mb-3">
              Want to temporarily stop all data sharing? You can pause monitoring in your desktop app
              at any time. When paused, no wellness data will be collected or shared.
            </p>
            <p className="text-sm text-muted-slate">
              Additionally, you can enable &quot;Privacy Mode&quot; in the desktop app to continue monitoring
              for your own insights without sharing data with your team.
            </p>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
